import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import { Lock, HeartHandshake, Loader2, Gift, LogOut, ShieldCheck } from "lucide-react";

const PIN_STORAGE_KEY = "mttApp:csrPin";
const CSR_BADGE = "🤝 CSR (Corporate Social Responsibility) Gifted Item";

/**
 * Iteration 291 — CSR Family Support Hub (/csr).
 *
 * A PIN-protected gifting section, fully separate from the public FREE
 * category. Any visitor with a valid 4-digit PIN can browse gifted items
 * at £0.00; CLAIMING an item requires a logged-in account (the claim
 * records the account ID for audit + delivery tracking). Double-claims
 * are rejected with a friendly "already claimed" state.
 */
export default function CsrHub() {
  const { user } = useAuth();
  const [pin, setPin] = useState(() =>
    (typeof window !== "undefined" && sessionStorage.getItem(PIN_STORAGE_KEY)) || ""
  );
  const [pinInput, setPinInput] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [items, setItems] = useState(null);
  const [itemsError, setItemsError] = useState("");
  const [claiming, setClaiming] = useState("");
  const [claimMsgs, setClaimMsgs] = useState({});
  const [loginPromptFor, setLoginPromptFor] = useState("");
  // Iteration 292 — community landfill-diversion counter from the hub feed.
  const [communityKg, setCommunityKg] = useState(null);

  const loadItems = async (activePin) => {
    setItemsError("");
    try {
      const { data } = await api.get("/csr/items", { params: { pin: activePin } });
      setItems(data?.items || []);
      setCommunityKg(typeof data?.community_weight_diverted_kg === "number" ? data.community_weight_diverted_kg : null);
    } catch (e) {
      if (e?.response?.status === 401) {
        // Stored PIN expired or was revoked — drop back to the gate.
        sessionStorage.removeItem(PIN_STORAGE_KEY);
        setPin("");
        setItems(null);
        setUnlockError("That PIN has expired — please ask your community partner for a new one.");
      } else {
        setItemsError(formatApiError(e) || "Couldn't load the hub — please try again.");
      }
    }
  };

  useEffect(() => { if (pin) loadItems(pin); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (e) => {
    e.preventDefault();
    const candidate = pinInput.trim();
    if (candidate.length !== 4) return;
    setUnlocking(true);
    setUnlockError("");
    try {
      await api.post("/csr/access", { pin: candidate });
      sessionStorage.setItem(PIN_STORAGE_KEY, candidate);
      setPin(candidate);
      setPinInput("");
      await loadItems(candidate);
    } catch (e2) {
      setUnlockError(
        e2?.response?.status === 401
          ? "Invalid or expired PIN — please check with your community partner."
          : (formatApiError(e2) || "Couldn't validate that PIN — please try again.")
      );
    } finally {
      setUnlocking(false);
    }
  };

  const lock = () => {
    sessionStorage.removeItem(PIN_STORAGE_KEY);
    setPin("");
    setItems(null);
    setClaimMsgs({});
    setLoginPromptFor("");
  };

  const claim = async (item) => {
    // Claiming requires a logged-in account — guests get the sign-in prompt.
    if (!user) {
      setLoginPromptFor(item.listing_id);
      return;
    }
    setClaiming(item.listing_id);
    try {
      await api.post("/csr/claim", { pin, listing_id: item.listing_id });
      setItems((prev) => (prev || []).map((it) => (
        it.listing_id === item.listing_id ? { ...it, claimed: true } : it
      )));
      setClaimMsgs((prev) => ({
        ...prev,
        [item.listing_id]: { type: "ok", text: "Item claimed! We'll be in touch to arrange collection." },
      }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setItems((prev) => (prev || []).map((it) => (
          it.listing_id === item.listing_id ? { ...it, claimed: true } : it
        )));
        setClaimMsgs((prev) => ({
          ...prev,
          [item.listing_id]: { type: "err", text: "This item has just been claimed by someone else." },
        }));
      } else if (status === 401) {
        setLoginPromptFor(item.listing_id);
      } else {
        setClaimMsgs((prev) => ({
          ...prev,
          [item.listing_id]: { type: "err", text: formatApiError(e) || "Couldn't claim this item — please try again." },
        }));
      }
    } finally {
      setClaiming("");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="csr-hub-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-20">
        <div className="max-w-2xl">
          <div className="pill bg-teal-100 text-teal-800 inline-flex items-center gap-1.5">
            <HeartHandshake className="w-3.5 h-3.5" /> Community Support
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3">CSR Family Support Hub</h1>
          <p className="text-slate-600 mt-2">
            Gifted items from our corporate partners, free to families referred by community support partners.
          </p>
        </div>

        {!pin ? (
          <div className="card-base p-6 md:p-8 max-w-md mt-8" data-testid="csr-gate-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-brandTeal/10 text-brandTeal inline-flex items-center justify-center">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-semibold text-brandSlate">Enter your access PIN</div>
                <div className="text-xs text-slate-500">PIN-protected access for community support partners</div>
              </div>
            </div>
            <form onSubmit={unlock} className="space-y-3" data-testid="csr-gate-form">
              <input
                className="input-base text-center text-2xl font-mono tracking-[0.5em]"
                placeholder="••••"
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]{4}"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                data-testid="csr-pin-input"
              />
              {unlockError && (
                <div className="text-xs text-rose-600" data-testid="csr-unlock-error">{unlockError}</div>
              )}
              <button
                type="submit"
                disabled={unlocking || pinInput.trim().length !== 4}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="csr-unlock-btn"
              >
                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Unlock the hub
              </button>
            </form>
            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
              This section is separate from the public free listings. If you don't have a PIN,
              speak to your community support partner or <Link to="/contact" className="text-brandTeal font-semibold" data-testid="csr-contact-link">contact us</Link>.
            </p>
          </div>
        ) : (
          <div className="mt-8">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="csr-unlocked-note">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Hub unlocked — gifted items are free to claim.
              </div>
              {typeof communityKg === "number" && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="csr-community-impact">
                  <HeartHandshake className="w-3.5 h-3.5" /> Total Community Waste Diverted: {communityKg.toFixed(1)} kg
                </div>
              )}
              <button
                type="button"
                onClick={lock}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600 transition"
                data-testid="csr-lock-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Lock the hub
              </button>
            </div>

            {itemsError && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800 mb-4" data-testid="csr-items-error">{itemsError}</div>
            )}

            {items === null ? (
              <div className="card-base p-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="csr-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading gifted items…
              </div>
            ) : items.length === 0 ? (
              <div className="card-base p-10 text-center" data-testid="csr-empty">
                <Gift className="w-6 h-6 mx-auto text-slate-400" />
                <div className="font-bold text-slate-700 mt-2">No gifted items right now</div>
                <p className="text-xs text-slate-500 mt-1">New items from our CSR partners will appear here — check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="csr-grid">
                {items.map((it) => (
                  <div key={it.listing_id} className={`card-base overflow-hidden ${it.claimed ? "opacity-75" : ""}`} data-testid={`csr-item-${it.listing_id}`}>
                    <div className="aspect-square bg-slate-100 relative">
                      <ListingPhoto src={it.photo} alt={it.title} className={`w-full h-full object-cover ${it.claimed ? "grayscale" : ""}`} />
                      {it.claimed && (
                        <div className="absolute inset-x-0 bottom-0 bg-slate-800/90 text-white px-3 py-2 text-center text-xs font-bold uppercase tracking-wider" data-testid={`csr-claimed-${it.listing_id}`}>
                          Claimed
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-teal-100 text-teal-900 border-teal-200 mb-2"
                        data-testid={`csr-badge-${it.listing_id}`}
                      >
                        {it.csr_badge || CSR_BADGE}
                      </span>
                      <div className="font-display text-lg font-medium text-slate-900 line-clamp-1" data-testid={`csr-title-${it.listing_id}`}>{it.title}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-2xl font-display font-semibold text-teal-700" data-testid={`csr-price-${it.listing_id}`}>£0.00</div>
                        {it.condition && <div className="text-xs text-slate-500 capitalize">{String(it.condition).replace(/_/g, " ")}</div>}
                      </div>
                      <div className="mt-3">
                        {!it.claimed && (
                          <button
                            type="button"
                            onClick={() => claim(it)}
                            disabled={claiming === it.listing_id}
                            className="btn-primary w-full !py-2 text-sm inline-flex items-center justify-center gap-2"
                            data-testid={`csr-claim-${it.listing_id}`}
                          >
                            {claiming === it.listing_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                            Claim this item
                          </button>
                        )}
                        {claimMsgs[it.listing_id] && (
                          <div
                            className={`mt-2 text-xs rounded-xl px-3 py-2 ${claimMsgs[it.listing_id].type === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}
                            data-testid={`csr-claim-msg-${it.listing_id}`}
                          >
                            {claimMsgs[it.listing_id].text}
                          </div>
                        )}
                        {loginPromptFor === it.listing_id && (
                          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="csr-login-prompt">
                            Please log in or create a free account to claim this item — your PIN stays unlocked on this device.
                            <Link to="/auth" className="block mt-1 font-bold text-brandPinkDark" data-testid="csr-login-link">Log in / Sign up →</Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
